import * as React from 'react';
import styled from 'styled-components';

import { getSemanticAttributes } from '../../internals/semanticProps';
import { rem } from '../../tokens/spacing';
export interface ToggleProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'type'> {
  label?: string;
  children?: React.ReactNode;
  semanticId?: string;
}

const ToggleWrapper = styled.div`
  position: relative;
  display: flex;
  gap: 10px;
  align-items: center;
  min-width: ${rem(30)};

  input {
    display: none;
  }

  input + label {
    background: var(--ui-border);
    border-radius: ${rem(30)};
    padding: ${rem(2)};
    transition: all 0.4s ease;
    outline: 0;
    display: block;
    width: ${rem(44)};
    height: ${rem(24)};
    position: relative;
    cursor: pointer;
    user-select: none;
  }

  input + label:after {
    position: relative;
    display: block;
    content: '';
    width: ${rem(20)};
    height: 100%;
    border-radius: 50%;
    background: var(--ui-surface);
    transition: all 0.2s ease;
    left: 0;
    box-shadow: ${rem(0)} ${rem(0)} ${rem(2)} var(--ui-shadow-color, rgba(0, 0, 0, 0.1)),
      inset ${rem(0)} ${rem(1)} ${rem(3)} rgba(242, 242, 242, 0.72);
  }

  input:checked + label {
    background: var(--ui-status-success);
  }

  input:checked + label:after {
    left: 50%;
  }

  input:disabled + label {
    background: var(--ui-background-muted);
    cursor: not-allowed;
    opacity: 1;
  }

  input:disabled:checked + label {
    background: var(--ui-text-subtle);
  }
`;

const LabelText = styled.label`
  display: block;
  font-weight: 400;
  line-height: ${rem(22)};
  font-size: ${rem(16)};
  cursor: pointer;
`;

export const Toggle = React.forwardRef<HTMLInputElement, ToggleProps>(function Toggle(
  { label, children, semanticId, id, name, className, disabled, ...rest },
  ref,
) {
  const { id: _semanticId, ...semanticProps } = getSemanticAttributes(semanticId);
  const inputId = id ?? name ?? 'toggle';

  return (
    <ToggleWrapper className={className} {...semanticProps}>
      <input
        ref={ref}
        type="checkbox"
        id={inputId}
        name={name}
        disabled={disabled}
        {...rest}
      />
      <label htmlFor={inputId} />
      {label != null && label !== '' && (
        <LabelText htmlFor={inputId}>{label}</LabelText>
      )}
    </ToggleWrapper>
  );
});

Toggle.displayName = 'Toggle';
